import { JsonObject, JsonProperty } from "typescript-json-serializer";
import {AuthoringInfoConfiguration} from "./authoring-info-configuration";
import {SymbolConfiguration, FillSymbolConfiguration, PolygonSymbol3DConfiguration} from "./symbol-configuration";
import {
    VisualVariableConfiguration,
    ColorVariableConfiguration,
    OpacityVariableConfiguration,
    RotationVariableConfiguration,
    SizeVariableConfiguration
} from "./visual-variable-configuration";

// A predicate function use to determine what is the symbol type
const visualVariablesTypePredicate = (vv: any) => {
    if (vv.type === "opacity") {
        return OpacityVariableConfiguration;
    }
    if (vv.type === "color") {
        return ColorVariableConfiguration;
    }
    if (vv.type === "size") {
        return SizeVariableConfiguration;
    }
    if (vv.type === "rotation") {
        return RotationVariableConfiguration;
    }

    return SizeVariableConfiguration;
};

@JsonObject()
export class RendererConfiguration {
    /**
     * Authoring metadata only included in renderers generated from one of the Smart Mapping creator methods, such as
     * [sizeRendererCreator.createContinuousRenderer()](https://developers.arcgis.com/javascript/latest/api-reference/esri-smartMapping-renderers-size.html#createContinuousRenderer)
     * or
     * [colorRendererCreator.createContinuousRenderer()](https://developers.arcgis.com/javascript/latest/api-reference/esri-smartMapping-renderers-color.html#createContinuousRenderer).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-Renderer.html#authoringInfo)
     */
    @JsonProperty()
    declare authoringInfo: AuthoringInfoConfiguration;
    /**
     * The renderer type.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-Renderer.html#type)
     */
    @JsonProperty()
    declare type: "class-breaks" | "dictionary" | "dot-density" | "heatmap" | "simple" | "unique-value";

    /** ------ from VisualVariablesMixin --- */
    /**
     * An array of [VisualVariable](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-VisualVariable.html) objects.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-mixins-VisualVariablesMixin.html#visualVariables)
     */
    @JsonProperty({type: visualVariablesTypePredicate})
    declare visualVariables?: VisualVariableConfiguration[];

    constructor() {}
}

@JsonObject()
export class SimpleRendererConfiguration extends RendererConfiguration {
    /**
     * The label for the renderer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-SimpleRenderer.html#label)
     */
    @JsonProperty()
    declare label: string;
    /**
     * The symbol used by the renderer to visualize all features in the layer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-SimpleRenderer.html#symbol)
     */
    @JsonProperty()
    declare symbol: SymbolConfiguration;
    /**
     * The type of renderer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-SimpleRenderer.html#type)
     */
    @JsonProperty()
    declare type: "simple";

    constructor() {
        super();
    }
}

@JsonObject()
export class UniqueValueRendererLegendOptionsConfiguration {
    /**
     * Describes the variable driving the visualization.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#legendOptions)
     */
    @JsonProperty()
    declare title?: string;

    constructor() {}
}

@JsonObject()
export class UniqueValueInfoConfiguration {
    /**
     * Describes the [value](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-UniqueValueInfo.html#value) represented by the [symbol](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-UniqueValueInfo.html#symbol).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-UniqueValueInfo.html#label)
     */
    @JsonProperty()
    declare label?: string;

    /**
     * Defines the symbol used to render features with the provided [value](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-UniqueValueInfo.html#value).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-UniqueValueInfo.html#symbol)
     */
    @JsonProperty()
    declare symbol: SymbolConfiguration;

    /**
     * Features with this value will be rendered with the given [symbol](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-UniqueValueInfo.html#symbol).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-support-UniqueValueInfo.html#value)
     */
    @JsonProperty()
    declare value: string | number;

    constructor() {}
}

@JsonObject()
export class UniqueValueRendererConfiguration extends RendererConfiguration {
    /**
     * This property is only relevant when symbolizing polygon features with marker symbols (or [IconSymbol3DLayer](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-IconSymbol3DLayer.html)) in the [uniqueValueInfos](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#uniqueValueInfos) of this renderer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#backgroundFillSymbol)
     */
    @JsonProperty()
    declare backgroundFillSymbol?: FillSymbolConfiguration | (PolygonSymbol3DConfiguration & { type: "polygon-3d" });
    /**
     * The label used in the [Legend](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Legend.html) to describe features assigned the [default symbol](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#defaultSymbol).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#defaultLabel)
     */
    @JsonProperty()
    declare defaultLabel?: string;
    /**
     * The symbol used to draw all features with values not referenced by [uniqueValueInfos](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#uniqueValueInfos) or [uniqueValueGroups](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#uniqueValueGroups).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#defaultSymbol)
     */
    @JsonProperty()
    declare defaultSymbol?: SymbolConfiguration;
    /**
     * The name of the attribute field containing types or categorical values referenced in [uniqueValueInfos](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#uniqueValueInfos) or [uniqueValueGroups](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#uniqueValueGroups).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#field)
     */
    @JsonProperty()
    declare field?: string;
    /**
     * Specifies the name of an additional attribute field used to categorize features.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#field2)
     */
    @JsonProperty()
    declare field2?: string;
    /**
     * Specifies the name of a third attribute field used to categorize features.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#field3)
     */
    @JsonProperty()
    declare field3?: string;
    /**
     * A string used as a separator between the values in the legend if multiple attribute fields are used to categorize values.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#fieldDelimiter)
     */
    @JsonProperty()
    declare fieldDelimiter?: string;
    /**
     * An object providing options for displaying the renderer in the Legend.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#legendOptions)
     */
    @JsonProperty()
    declare legendOptions?: UniqueValueRendererLegendOptionsConfiguration;

    /**
     * Indicates whether the order of the classes or [uniqueValueInfos](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#uniqueValueInfos) in the renderer definition should be used for the feature drawing order of the layer.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#orderByClassesEnabled)
     */
    @JsonProperty()
    declare orderByClassesEnabled?: boolean;

    /**
     * The type of Renderer.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#type)
     */
    declare readonly type: "unique-value";
    /**
     * An array of objects defining groups of unique values.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#uniqueValueGroups)
     */
    // declare uniqueValueGroups?: UniqueValueGroupProperties[];
    /**
     * Defines categories and their corresponding symbols based on a set of values expected from the provided [field](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#field) or [valueExpression](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#valueExpression).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#uniqueValueInfos)
     */
    @JsonProperty()
    declare uniqueValueInfos?: UniqueValueInfoConfiguration[];
    /**
     * An [Arcade](https://developers.arcgis.com/javascript/latest/arcade/) expression following the specification defined by the [Arcade Visualization Profile](https://developers.arcgis.com/javascript/latest/arcade/#visualization).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#valueExpression)
     */
    @JsonProperty()
    declare valueExpression?: string;
    /**
     * The title identifying and describing the associated
     * [Arcade](https://developers.arcgis.com/javascript/latest/arcade/) expression as defined in the
     * [valueExpression](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#valueExpression) property.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-UniqueValueRenderer.html#valueExpressionTitle)
     */
    @JsonProperty()
    declare valueExpressionTitle?: string;

    constructor() {
        super();
    }
}
